import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    message,
    Spin,
    Card,
    Statistic,
    Row,
    Col,
    Badge,
    ConfigProvider,
    Switch,
    theme
} from 'antd';
import axiosInstance from '../../../axiosInstance';
import { motion } from 'framer-motion';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    Legend,
    Label
} from 'recharts';

const Customers = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isLedgerModalVisible, setIsLedgerModalVisible] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [ledgerData, setLedgerData] = useState({ invoices: [], receipts: [], summary: {} });
    const [ledgerLoading, setLedgerLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [form] = Form.useForm();
    const [darkMode, setDarkMode] = useState(false);

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchCustomers = async (query = '') => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/customers', { params: { search: query } });
            setCustomers(response.data.customers || []);
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message === "No customers found") {
                setCustomers([]);
            } else {
                message.error('Failed to load customers.');
            }
        } finally {
            setLoading(false);
        }
    };
    

    const fetchLedger = async (customerId) => {
        setLedgerLoading(true);
        try {
            const response = await axiosInstance.get(`customer-ledger/${customerId}`);
            setLedgerData(response.data);
        } catch {
            message.error('Failed to fetch ledger.');
        } finally {
            setLedgerLoading(false);
        }
    };

    const handleSearch = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        fetchCustomers(query);
    };

    const handleAddOrEditCustomer = async () => {
        try {
            const values = await form.validateFields();
            if (editingCustomer) {
                await axiosInstance.put(`/customers/${editingCustomer.id}`, values);
                setCustomers(customers.map(c => (c.id === editingCustomer.id ? { ...c, ...values } : c)));
                message.success('Customer updated successfully!');
            } else {
                const response = await axiosInstance.post('/customers', values);
                setCustomers([...customers, response.data.customer]);
                message.success('Customer added successfully!');
            }
            setIsModalVisible(false);
            setEditingCustomer(null);
            form.resetFields();
        } catch {
            message.error('Failed to save customer.');
        }
    };

    const handleEdit = (customer) => {
        setEditingCustomer(customer);
        form.setFieldsValue(customer);
        setIsModalVisible(true);
    };

    const handleViewLedger = async (customerId) => {
        setSelectedCustomerId(customerId);
        await fetchLedger(customerId);
        setIsLedgerModalVisible(true);
    };

    const statusBadge = (status) => {
        let color = 'default';
        if (status === 'Paid') color = 'green';
        else if (status === 'Pending') color = 'orange';
        else if (status === 'Overdue') color = 'red';
        else if (status === 'drafted') color = 'blue';
        else if (status === 'processed') color = 'purple';
        return <Badge color={color} text={status} />;
    };

    const commonColumns = [
        { title: 'Date', dataIndex: 'date', key: 'date' },
        { title: 'Total', dataIndex: 'total', key: 'total' },
        { title: 'Discount', dataIndex: 'discount', key: 'discount' },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: statusBadge
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <a href={`/sales/view/${record.id}`} target="_blank" rel="noreferrer">
                    View Sale
                </a>
            ),
        },
    ];

    const pieData = [
        {
            name: 'Total Invoiced',
            value: ledgerData.summary.total_invoiced || 0,
            color: '#1890ff',
        },
        {
            name: 'Total Paid',
            value: ledgerData.summary.total_paid || 0,
            color: '#52c41a',
        },
        {
            name: 'Discount',
            value: ledgerData.summary.total_discount || 0,
            color: '#fa8c16',
        },
        {
            name: 'Outstanding',
            value: ledgerData.summary.outstanding_balance || 0,
            color: '#f5222d',
        },
    ];

    return (
        <ConfigProvider theme={{ algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
            <div style={{ padding: 16 }}>
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col xs={24} md={16}>
                        <Input
                            placeholder="Search customers"
                            value={searchQuery}
                            onChange={handleSearch}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                        <Button
                            type="primary"
                            onClick={() => {
                                setEditingCustomer(null);
                                form.resetFields();
                                setIsModalVisible(true);
                            }}
                        >
                            Add Customer
                        </Button>
                        <Switch
                            checked={darkMode}
                            onChange={setDarkMode}
                            checkedChildren="Dark"
                            unCheckedChildren="Light"
                            style={{ marginLeft: 16 }}
                        />
                    </Col>
                </Row>


                <div style={{ overflowX: 'auto', marginTop: 16 }}>
                    <Table
                        dataSource={customers.map(customer => ({ key: customer.id, ...customer }))}
                        columns={[
                            { title: 'Name', dataIndex: 'name', key: 'name' },
                            { title: 'Email', dataIndex: 'email', key: 'email' },
                            { title: 'Phone', dataIndex: 'phone', key: 'phone' },
                            {
                                title: 'Action',
                                key: 'action',
                                render: (_, record) => (
                                    <>
                                        <Button type="link" onClick={() => handleEdit(record)} style={{ marginRight: 8 }}>
                                            Edit
                                        </Button>
                                        <Button type="link" onClick={() => handleViewLedger(record.id)}>
                                            View Ledger
                                        </Button>
                                    </>
                                ),
                            },
                        ]}
                        loading={loading}
                        pagination={{ pageSize: 25 }}
                        scroll={{ x: 'max-content' }}
                    />
                </div>

                {/* Modal for Add/Edit */}
                <Modal
                    title={editingCustomer ? 'Edit Customer' : 'Add Customer'}
                    open={isModalVisible}
                    onOk={form.submit}
                    onCancel={() => setIsModalVisible(false)}
                    okText={editingCustomer ? 'Update' : 'Add'}
                    cancelText="Cancel"
                >
                    <Form form={form} layout="vertical" onFinish={handleAddOrEditCustomer}>
                        <Form.Item
                            name="name"
                            label="Name"
                            rules={[{ required: true, message: 'Please input the customer name!' }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item name="email" label="Email">
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="phone"
                            label="Phone"
                            rules={[{ required: true, message: 'Please input a valid phone number!' }]}
                        >
                            <Input />
                        </Form.Item>
                    </Form>
                </Modal>

                {/* Ledger Modal */}
                <Modal
                    title="Customer Ledger"
                    open={isLedgerModalVisible}
                    onCancel={() => setIsLedgerModalVisible(false)}
                    footer={null}
                    width={1000}
                    style={{ top: 40 }}
                >
                    {ledgerLoading ? (
                        <Spin />
                    ) : (
                        <>
                            {/* Summary Cards */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                {pieData.map((item, i) => (
                                    <Col xs={24} sm={12} md={6} key={i}>
                                        <motion.div whileHover={{ scale: 1.05 }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.1 }}>
                                            <Card bordered={false}>
                                                <Statistic
                                                    title={<span style={{ color: item.color }}>{item.name}</span>}
                                                    value={item.value}
                                                    precision={2}
                                                    valueStyle={{ color: item.color }}
                                                />
                                            </Card>
                                        </motion.div>
                                    </Col>
                                ))}
                            </Row>

                            {/* Animated Pie Chart with Legend */}
                            <div style={{ height: 320, marginBottom: 24 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            dataKey="value"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={100}
                                            innerRadius={50}
                                            label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                                        >
                                            {pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                            <Label value="Ledger Summary" position="centerBottom" fontSize={14} fill="#888" />
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            <h3>Invoices</h3>
                            <Table
                                dataSource={ledgerData.invoices.map(i => ({ key: i.id, ...i }))}
                                columns={commonColumns}
                                pagination={false}
                                scroll={{ x: true }}
                                style={{ marginBottom: 32 }}
                            />

                            <h3>Receipts</h3>
                            <Table
                                dataSource={ledgerData.receipts.map(r => ({ key: r.id, ...r }))}
                                columns={commonColumns}
                                pagination={false}
                                scroll={{ x: true }}
                            />
                        </>
                    )}
                </Modal>
            </div>
        </ConfigProvider>
    );
};

export default Customers;
